import { useEffect, useState } from 'react';
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { bootstrap, signOut, type CurrentUser } from './lib/auth.js';
import { Login } from './pages/Login.js';
import { ForgotPassword } from './pages/ForgotPassword.js';
import { ResetPassword } from './pages/ResetPassword.js';
import { Admin } from './pages/Admin.js';
import { PostLogin } from './pages/PostLogin.js';
import { Logout } from './pages/Logout.js';

export function App() {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    bootstrap().then((u) => {
      setUser(u);
      setLoading(false);
    });
  }, []);

  // Listen for sign-in events from the Login page
  useEffect(() => {
    const handler = () => {
      bootstrap().then(setUser);
    };
    window.addEventListener('redwood-auth-changed', handler);
    return () => window.removeEventListener('redwood-auth-changed', handler);
  }, []);

  return (
    <div className="flex flex-col min-h-full">
      <Header user={user} onSignOut={async () => { await signOut(); setUser(null); }} />
      <main className="flex-1">
        {loading ? (
          <div className="p-12 text-brand-500">Loading…</div>
        ) : (
          <Routes>
            <Route path="/" element={<RootRoute user={user} />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/post-login" element={<PostLogin />} />
            <Route path="/logout" element={<Logout />} />
            <Route
              path="/admin"
              element={user ? <Admin user={user} /> : <Navigate to="/login" replace />}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
      </main>
    </div>
  );
}

function RootRoute({ user }: { user: CurrentUser | null }) {
  // If a redirect param is present and the user is signed in, hand off to PostLogin
  // which validates the redirect URL and bounces. If not signed in, send them to login
  // (preserving the redirect param so PostLogin gets it after login).
  const params = new URLSearchParams(useLocation().search);
  const redirect = params.get('redirect');
  if (user && redirect) return <Navigate to={`/post-login?redirect=${encodeURIComponent(redirect)}`} replace />;
  if (user) return <Navigate to="/admin" replace />;
  if (redirect) return <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />;
  return <Navigate to="/login" replace />;
}

function Header({
  user,
  onSignOut,
}: {
  user: CurrentUser | null;
  onSignOut: () => void | Promise<void>;
}) {
  return (
    <header className="border-b border-brand-100 px-12 py-3 flex items-center justify-between bg-white shrink-0">
      <Link to="/" className="font-display font-bold text-brand-700 text-lg leading-none hover:text-brand-800">
        Redwood Auth
      </Link>
      {user && (
        <div className="flex items-center gap-4 text-sm">
          <span className="text-brand-500">{user.name || user.email}</span>
          {user.groups.includes('auth:admin') && (
            <Link to="/admin" className="text-brand-700 hover:underline">
              User management
            </Link>
          )}
          <button
            onClick={() => onSignOut()}
            className="text-brand-600 hover:underline"
          >
            Sign out
          </button>
        </div>
      )}
    </header>
  );
}
